/* eslint-disable @typescript-eslint/no-explicit-any */
/** Quotation engine: list tab/page, builder, and document preview — ported from `quotation.jsx`. */
import { useState } from 'react';
import React from 'react';
import { useData, useDataActions } from '../context/AppDataContext';
import { useCan } from '../context/AuthContext';
import { apiClient, ApiClientError } from '../lib/apiClient';
import { Ico, HerosMark, fmt, useToast } from '../components/ui';

export function QuotationTab({ p, openQuotation }: { p: any; openQuotation: (id?: string) => void }) {
  const D = useData();
  const { reload } = useDataActions();
  const toast = useToast();
  const canCreate = useCan('quote.create') === 'allow';
  const qs = D.quotations.filter((q) => q.projectId === p.id);
  const [creating, setCreating] = useState(false);

  // '+ New quotation' — create a real Draft row (three-tier fallback), then open it.
  // 1) Duplicate the latest version's section snapshot; else 2) price the project's
  // builder design snapshot; else 3) an empty-section draft.
  const createNew = async () => {
    if (creating || !canCreate) return;
    setCreating(true);
    try {
      const latest = [...qs].sort((a, b) => (a.number < b.number ? 1 : -1))[0];
      let created;
      if (latest) {
        created = await apiClient.quotations.create({
          projectId: p.id,
          sections: latest.sections,
          version: 'v' + (qs.length + 1) + '.0 (Draft)',
        });
      } else {
        created = null;
        if (p.builderJson) {
          try {
            created = await apiClient.quotations.create({ projectId: p.id, form: JSON.parse(p.builderJson) });
          } catch { created = null; /* bad/incompatible snapshot → fall through to an empty draft */ }
        }
        if (!created) created = await apiClient.quotations.create({ projectId: p.id, sections: [] });
      }
      await reload();
      openQuotation(created.id);
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Could not create quotation') : 'Could not create quotation');
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="card">
      <div className="card-h">
        <div>
          <h3>Quotations for {p.code}</h3>
          <div className="sub">Versioned · each preserves its own pricing snapshot</div>
        </div>
        <button className="btn primary sm" disabled={creating || !canCreate} onClick={() => void createNew()}><Ico.plus /> {creating ? 'Creating…' : 'New quotation'}</button>
      </div>
      <div className="card-b flush">
        {qs.length === 0 ? (
          <div style={{ padding: 24, color: 'var(--text-2)', fontSize: 12.5 }}>No quotations yet for this project.</div>
        ) : (
          <table>
            <thead><tr><th>Quote #</th><th>Version</th><th>Submitted</th><th>Grand total</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {qs.map((q) => (
                <tr key={q.id} className="hover-row" onClick={() => openQuotation(q.id)}>
                  <td className="mono">{q.number}</td>
                  <td>{q.version}</td>
                  <td className="mono" style={{ fontSize: 11.5, color: 'var(--text-2)' }}>{q.submitted}</td>
                  <td className="num tabular" style={{ fontWeight: 600 }}>{fmt.thb(q.grandTotal)}</td>
                  <td>{q.version.includes('Final') ? <span className="chip" data-tone="green"><span className="dot" />Final</span> : <span className="chip" data-tone="amber"><span className="dot" />Draft</span>}</td>
                  <td><button className="btn ghost icon sm" title="Open quotation" onClick={(e) => { e.stopPropagation(); openQuotation(q.id); }}><Ico.dot3 /></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

export function QuotationBuilder({ quotationId, onClose }: { quotationId: string | null; onClose: () => void }) {
  const D = useData();
  const { reload } = useDataActions();
  const toast = useToast();
  const canEdit = useCan('quote.create') === 'allow';
  const canAdjust = useCan('quote.adjust') === 'allow';
  // Unknown/missing id renders the inert empty stub (quote.id==='' disables Save);
  // NO cross-project fallback — opening a bad id must not surface another project's quote.
  const initial = D.quotations.find((q) => q.id === quotationId) || {
    id: '', projectId: '', number: '—', version: 'v1.0 (Draft)', submitted: '', sections: [],
    adjustments: { discount: 0, additional: 0, vat: 0, rounding: 0 }, grandTotal: 0,
  };
  const [quote, setQuote] = useState<any>(() => JSON.parse(JSON.stringify(initial)));
  const [view, setView] = useState('builder');
  const [saving, setSaving] = useState(false);
  const project = D.projects.find((p) => p.id === quote.projectId);
  const projectVersions = D.quotations
    .filter((q) => q.projectId === quote.projectId)
    .sort((a, b) => (a.number < b.number ? 1 : -1));

  // Excluded lines (included === false) contribute nothing — this single source
  // of truth propagates to the summary card, grand total, saved section totals,
  // the PDF and the document preview.
  const sectionTotal = (s: any) => s.items.reduce((sum: number, it: any) => sum + (it.included === false ? 0 : (it.total || 0)), 0);
  const sectionsTotal = quote.sections.reduce((sum: number, s: any) => sum + sectionTotal(s), 0);
  // The builder is editable for a real, non-Final draft that the user may edit.
  const editable = canEdit && !!quote.id && !String(quote.version || '').includes('Final');
  const rand = () => Math.random().toString(36).slice(2);
  const setIncluded = (sectId: string, itemId: string, included: boolean) => {
    setQuote((q: any) => ({
      ...q,
      sections: q.sections.map((s: any) => (s.id !== sectId ? s : {
        ...s,
        items: s.items.map((it: any) => (it.id !== itemId ? it : { ...it, included })),
      })),
    }));
  };
  const addItem = (sectId: string) => {
    const name = window.prompt('Item name');
    if (!name || !name.trim()) return;
    setQuote((q: any) => ({
      ...q,
      sections: q.sections.map((s: any) => (s.id !== sectId ? s : {
        ...s,
        items: [...s.items, { id: 'it_' + rand(), code: '', name: name.trim(), qty: 1, unit: 'unit', price: 0, total: 0, included: true }],
      })),
    }));
  };
  const addSection = () => {
    const title = window.prompt('Section title', 'Additional services');
    if (title === null) return;
    setQuote((q: any) => ({ ...q, sections: [...q.sections, { id: 's_' + rand(), title: title.trim() || 'Additional services', total: 0, items: [] }] }));
  };
  const adj = quote.adjustments || { discount: 0, additional: 0, vat: 0, rounding: 0 };
  // The formula-calculated original total (never overwritten by a manual adjustment).
  const originalTotal = sectionsTotal;
  const grandTotal = adj.mode === 'custom' && typeof adj.customFinal === 'number'
    ? Math.max(0, Math.round(adj.customFinal))
    : Math.max(0, sectionsTotal - (adj.discount || 0) + (adj.additional || 0) + (adj.vat || 0) + (adj.rounding || 0));
  const isManualAdj = (adj.mode && adj.mode !== 'none') || grandTotal !== originalTotal;

  const saveVersion = async () => {
    if (!quote.id) { toast('No quotation selected'); return; }
    if (canAdjust && isManualAdj && !String(adj.reason || '').trim()) {
      toast('A reason is required when manually adjusting the final price.');
      return;
    }
    setSaving(true);
    try {
      // Persist section edits, then the manual adjustments.
      await apiClient.quotations.update(quote.id, {
        sections: quote.sections.map((s: any) => ({
          id: s.id, title: s.title, total: sectionTotal(s),
          items: s.items.map((it: any) => ({
            id: it.id, code: it.code, name: it.name, qty: it.qty, unit: it.unit, price: it.price, total: it.total, included: it.included,
          })),
        })),
      });
      if (canAdjust) await apiClient.quotations.adjustments(quote.id, adj);
      await reload();
      toast('Quotation version saved');
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Save failed') : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const setLifecycle = async (status: 'Final' | 'Archived') => {
    if (!quote.id) return;
    setSaving(true);
    try {
      await apiClient.quotations.lifecycle(quote.id, status);
      await reload();
      toast(`Quotation marked ${status}`);
    } catch (err) {
      toast(err instanceof ApiClientError ? (err.body?.message ?? 'Lifecycle change failed') : 'Lifecycle change failed');
    } finally {
      setSaving(false);
    }
  };

  const updateItem = (sectId: string, itemId: string, patch: any) => {
    setQuote((q: any) => ({
      ...q,
      sections: q.sections.map((s: any) => (s.id !== sectId ? s : {
        ...s,
        items: s.items.map((it: any) => (it.id !== itemId ? it : { ...it, ...patch, total: (patch.qty ?? it.qty) * (patch.price ?? it.price) })),
      })),
    }));
  };
  const updateAdj = (patch: any) => setQuote((q: any) => ({ ...q, adjustments: { ...adj, ...patch } }));

  const [pdfBusy, setPdfBusy] = useState(false);
  const exportPdf = async () => {
    setPdfBusy(true);
    try {
      const adjustments = [
        { label: 'Discount', amount: -(adj.discount || 0) },
        { label: 'Additional charge', amount: adj.additional || 0 },
        { label: 'VAT 7%', amount: adj.vat || 0 },
        { label: 'Rounding', amount: adj.rounding || 0 },
      ].filter((a) => a.amount !== 0);
      const blob = await apiClient.quotations.pdf({
        studyCode: project?.code || '',
        studyTitle: project?.title || '',
        quotationNumber: String(quote.number ?? ''),
        version: String(quote.version ?? ''),
        dateText: String(quote.submitted ?? ''),
        currency: 'THB',
        grandTotal: Math.round(grandTotal),
        subtotal: Math.round(sectionsTotal),
        sections: quote.sections.map((s: any) => ({
          title: s.title,
          total: Math.round(sectionTotal(s)),
          items: s.items.filter((it: any) => it.included !== false).map((it: any) => ({
            code: it.code, name: it.name, qty: it.qty, unit: it.unit,
            price: Math.round(it.price || 0), total: Math.round(it.total || 0),
          })),
        })),
        adjustments,
      });
      const slug = String(quote.number || project?.code || 'quotation').replace(/[^a-z0-9-]+/gi, '_');
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `Quotation_${slug}.pdf`; document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1500);
    } catch (err) {
      const m = err instanceof ApiClientError ? (err.body?.message ?? '') : String((err as any)?.message || '');
      toast(/not installed|Unavailable|503/i.test(m) ? 'Server LaTeX is unavailable — cannot generate the PDF.' : 'Quotation PDF failed — please try again.');
    } finally {
      setPdfBusy(false);
    }
  };

  const [refreshing, setRefreshing] = useState(false);
  const refreshFromPrices = async () => {
    if (!project?.builderJson) return;
    if (!window.confirm('Re-price this quotation from the current pricing libraries? Manual line edits in this session will be replaced.')) return;
    setRefreshing(true);
    try {
      const form = JSON.parse(project.builderJson);
      // Overlay the CURRENT active lab prices (matched by name) from the shared
      // catalog; quoteOverrides on the parsed form are left untouched.
      const priceByName = new Map<string, number>();
      for (const c of D.catalog) {
        if (c.kind === 'lab' && c.status === 'active' && typeof c.price === 'number') priceByName.set(c.name, c.price);
      }
      if (Array.isArray(form.labs)) {
        form.labs = form.labs.map((l: any) => (priceByName.has(l.name) ? { ...l, price: priceByName.get(l.name) } : l));
      }
      const res = await apiClient.quotations.compute(form);
      setQuote((q: any) => ({ ...q, sections: res.sections }));
      toast('Prices refreshed — review, then Save version to persist.');
    } catch (err) {
      if (err instanceof ApiClientError && err.status === 400) {
        toast('The saved design snapshot is incompatible with the pricing engine.');
      } else {
        toast('The saved design snapshot is incompatible with the pricing engine.');
      }
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="page-pad fadein" style={{ paddingBottom: 60 }}>
      <div style={{ marginBottom: 4 }}>
        <button className="btn ghost sm" onClick={onClose}><Ico.chevLeft /> Back</button>
      </div>
      <div className="page-head">
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span className="mono" style={{ fontSize: 12, color: 'var(--accent)', fontWeight: 700 }}>{project?.code}</span>
            <span style={{ color: 'var(--text-3)' }}>·</span>
            <span style={{ fontSize: 12, color: 'var(--text-2)' }}>Quote #{quote.number}</span>
            <span className="chip" data-tone={quote.version.includes('Final') ? 'green' : 'amber'}><span className="dot" />{quote.version}</span>
          </div>
          <h2 style={{ marginTop: 6 }}>Quotation builder</h2>
          <div className="sub">{project?.title}</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div className="seg">
            <button className={view === 'builder' ? 'on' : ''} onClick={() => setView('builder')}>Builder</button>
            <button className={view === 'preview' ? 'on' : ''} onClick={() => setView('preview')}>Document preview</button>
          </div>
          <button className="btn" disabled={pdfBusy} onClick={() => void exportPdf()}><Ico.dl /> {pdfBusy ? 'Generating…' : 'Export PDF'}</button>
          {canEdit && !quote.version?.includes('Final') && (
            <button className="btn" disabled={saving || !quote.id} onClick={() => void setLifecycle('Final')} title="Make this the project's active version">
              <Ico.check /> Mark Final
            </button>
          )}
          <button className="btn primary" disabled={saving || !canEdit || !quote.id} onClick={() => void saveVersion()}>
            <Ico.check /> {saving ? 'Saving…' : 'Save version'}
          </button>
        </div>
      </div>

      {view === 'builder' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 340px', gap: 16 }}>
          <div className="card">
            <div className="card-h"><h3>Service items &amp; pricing</h3><div className="sub">Edit qty / unit price live — totals recompute</div></div>
            <div className="card-b" style={{ padding: 0 }}>
              {quote.sections.map((s: any) => (
                <div key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                  <div style={{ padding: '10px 14px', background: 'var(--surface-alt)', fontWeight: 600, fontSize: 12.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>{s.title}</span>
                    <span className="tabular mono" style={{ color: 'var(--text-2)' }}>Subtotal {fmt.thb(sectionTotal(s))}</span>
                  </div>
                  <table>
                    <thead>
                      <tr>
                        <th style={{ width: 50 }}></th>
                        <th>Item</th>
                        <th className="num" style={{ width: 70 }}>Qty</th>
                        <th style={{ width: 60 }}>Unit</th>
                        <th className="num" style={{ width: 100 }}>Unit price</th>
                        <th className="num" style={{ width: 110 }}>Total</th>
                        <th style={{ width: 30 }}></th>
                      </tr>
                    </thead>
                    <tbody>
                      {s.items.map((it: any) => (
                        <tr key={it.id} style={{ opacity: it.included === false ? 0.55 : 1 }}>
                          <td><input type="checkbox" checked={it.included !== false} onChange={(e) => setIncluded(s.id, it.id, e.target.checked)} title={it.included === false ? 'Excluded from totals' : 'Included in totals'} /></td>
                          <td>
                            <div style={{ fontWeight: 500, display: 'flex', alignItems: 'baseline', gap: 6 }}>
                              {it.code && <span className="mono" style={{ color: 'var(--text-2)', fontSize: 11 }}>{it.code}</span>}
                              {it.name}
                            </div>
                          </td>
                          <td><input className="tabular mono" type="number" value={it.qty} style={{ textAlign: 'right', padding: '4px 6px' }} onChange={(e) => updateItem(s.id, it.id, { qty: +e.target.value || 0 })} /></td>
                          <td style={{ fontSize: 11, color: 'var(--text-2)' }}>{it.unit}</td>
                          <td><input className="tabular mono" type="number" value={it.price} style={{ textAlign: 'right', padding: '4px 6px' }} onChange={(e) => updateItem(s.id, it.id, { price: +e.target.value || 0 })} /></td>
                          <td className="num tabular mono" style={{ fontWeight: 600 }}>{fmt.thb(it.total)}</td>
                          <td></td>
                        </tr>
                      ))}
                      {editable && (
                        <tr>
                          <td colSpan={7} style={{ background: 'var(--bg)' }}>
                            <button className="btn ghost sm" onClick={() => addItem(s.id)}><Ico.plus /> Add item to section</button>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              ))}
              {editable && (
                <div style={{ padding: 12, display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn" onClick={addSection}><Ico.plus /> Add section</button>
                </div>
              )}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 14, position: 'sticky', top: 8, alignSelf: 'flex-start' }}>
            <div className="card">
              <div className="card-h"><h3>Summary</h3></div>
              <div className="card-b">
                {quote.sections.map((s: any) => (
                  <div key={s.id} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, padding: '4px 0', borderBottom: '1px dashed var(--border)' }}>
                    <span style={{ color: 'var(--text-2)' }}>{s.title.replace(/^\d+\.\s*/, '')}</span>
                    <span className="tabular mono">{fmt.thb(sectionTotal(s))}</span>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', fontWeight: 600, borderBottom: '1px solid var(--border)' }}>
                  <span>Original calculated total</span>
                  <span className="tabular mono">{fmt.thb(originalTotal)}</span>
                </div>

                {/* Manual final-price adjustment (brief Feature 5) — cap-gated. */}
                <div className="field" style={{ marginTop: 12 }}>
                  <label>Manual adjustment {!canAdjust && <span style={{ color: 'var(--text-3)' }}>(view only — needs PM/Admin)</span>}</label>
                  <select value={adj.mode || 'none'} disabled={!canAdjust} onChange={(e) => updateAdj({ mode: e.target.value })}>
                    <option value="none">None (use calculated total)</option>
                    <option value="discount">Discount</option>
                    <option value="markup">Markup / additional charge</option>
                    <option value="custom">Custom final price</option>
                  </select>
                </div>
                {adj.mode === 'custom' ? (
                  <div className="field" style={{ marginTop: 6 }}>
                    <label>Custom final price (฿)</label>
                    <input className="tabular mono" type="number" min="0" disabled={!canAdjust} value={adj.customFinal ?? originalTotal} onChange={(e) => updateAdj({ customFinal: Math.max(0, +e.target.value || 0) })} />
                  </div>
                ) : (
                  <>
                    <div className="field" style={{ marginTop: 6 }}>
                      <label>Discount (฿)</label>
                      <input className="tabular mono" type="number" disabled={!canAdjust} value={adj.discount} onChange={(e) => updateAdj({ discount: +e.target.value || 0 })} />
                    </div>
                    <div className="field" style={{ marginTop: 6 }}>
                      <label>Additional charge (฿)</label>
                      <input className="tabular mono" type="number" disabled={!canAdjust} value={adj.additional} onChange={(e) => updateAdj({ additional: +e.target.value || 0 })} />
                    </div>
                    <div className="field" style={{ marginTop: 6 }}>
                      <label>VAT 7% (฿)</label>
                      <input className="tabular mono" type="number" disabled={!canAdjust} value={adj.vat} onChange={(e) => updateAdj({ vat: +e.target.value || 0 })} />
                    </div>
                  </>
                )}
                {isManualAdj && (
                  <div className="field" style={{ marginTop: 6 }}>
                    <label>Reason for adjustment <span style={{ color: 'var(--danger, #b3261e)' }}>*</span></label>
                    <textarea value={adj.reason || ''} disabled={!canAdjust} onChange={(e) => updateAdj({ reason: e.target.value })} placeholder="Required — e.g. 10% institutional discount approved by Director" rows={2} />
                  </div>
                )}
                {isManualAdj && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11.5, color: 'var(--text-2)', marginTop: 6 }}>
                    <span>Manual adjustment</span>
                    <span className="tabular mono">{grandTotal >= originalTotal ? '+' : '−'}{fmt.thb(Math.abs(grandTotal - originalTotal))}</span>
                  </div>
                )}
                {adj.adjustedBy && adj.adjustedAt && (
                  <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 4 }}>
                    Adjusted by {D.team.find((u) => u.id === adj.adjustedBy)?.name || adj.adjustedBy} · {String(adj.adjustedAt).slice(0, 16).replace('T', ' ')}
                  </div>
                )}

                <hr className="rule" />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <span style={{ fontSize: 13, fontWeight: 600 }}>Final adjusted total</span>
                  <span className="tabular mono" style={{ fontSize: 20, fontWeight: 800, color: 'var(--navy)' }}>{fmt.thb(grandTotal)}</span>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-h"><h3>Pricing snapshot</h3></div>
              <div className="card-b" style={{ fontSize: 11.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
                This quotation stores its own pricing snapshot. Refreshing re-prices it from the current admin libraries.
                <div style={{ marginTop: 8 }}>
                  <button
                    className="btn sm"
                    style={{ width: '100%' }}
                    disabled={!project?.builderJson || refreshing}
                    title={!project?.builderJson ? 'No builder design snapshot for this project' : undefined}
                    onClick={() => void refreshFromPrices()}
                  >
                    {refreshing ? 'Refreshing…' : '↻ Refresh from current prices'}
                  </button>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-h"><h3>Version history</h3></div>
              <div className="card-b" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {projectVersions.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--text-2)' }}>No versions yet.</div>}
                {projectVersions.map((v) => {
                  const current = v.id === quote.id;
                  return (
                    <div
                      key={v.id}
                      onClick={() => setQuote(JSON.parse(JSON.stringify(v)))}
                      style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6, background: current ? 'var(--surface-alt)' : '#fff', cursor: 'pointer' }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontWeight: 600, fontSize: 12 }}>{v.version}</span>
                        <span className="mono" style={{ fontSize: 11, color: 'var(--text-2)' }}>{v.submitted}</span>
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-2)', marginTop: 2 }}>
                        #{v.number} · {fmt.thb(v.grandTotal)}{v.status ? ` · ${v.status}` : ''}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}

      {view === 'preview' && (
        <QuoteDocument quote={quote} project={project} grandTotal={grandTotal} />
      )}
    </div>
  );
}

export function QuoteDocument({ quote, project, grandTotal }: { quote: any; project: any; grandTotal: number }) {
  return (
    <div className="quote-doc">
      <div className="rule-top" />

      <div className="qhead">
        <div>
          <h1>Health Evidence and Research Operations (HEROs)</h1>
          <p>Department of Biomedical Informatics</p>
          <p>and Clinical Epidemiology (BioCE)</p>
          <p>Faculty of Medicine, Chiang Mai University</p>
          <p>Chiang Mai, Thailand 50200</p>
          <p>(+66) 53-936-150</p>
        </div>
        <div className="qlogo">
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--blue-rule)', textAlign: 'center', letterSpacing: '0.02em' }}>BioCE</div>
            <div style={{ fontSize: 8.5, color: 'var(--blue-rule)', textAlign: 'center' }}>Biomedical Informatics<br />and Clinical Epidemiology</div>
            <div style={{ marginTop: 4 }}><HerosMark size={64} /></div>
            <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--navy)' }}>HEROs</div>
          </div>
        </div>
      </div>

      <div className="qhead-divider" />

      <div className="qtitle">
        <h2>Analysis Quote</h2>
        <div className="sub">Submitted on {quote.submitted}</div>
      </div>

      <div className="qproject">
        <div>
          <h3>Project</h3>
          <div className="pbody">{project?.title}</div>
        </div>
        <div className="qno">
          Quote #
          <div className="num mono">{quote.number}</div>
        </div>
      </div>

      <div className="qtable-divider" />

      <div className="qtable">
        <div className="qhead-row"><span>Description</span><span>Total price</span></div>
        {quote.sections.map((s: any) => {
          const shown = s.items.filter((it: any) => it.included !== false);
          const subtotal = shown.reduce((sum: number, it: any) => sum + (it.total || 0), 0);
          return (
            <React.Fragment key={s.id}>
              <div className="qsec"><span>{s.title}</span><span>{fmt.thb(subtotal)}</span></div>
              {shown.length > 1 || (shown[0] && shown[0].code) ? (
                shown.map((it: any) => (
                  <div className="qrow" key={it.id}>
                    <span>{it.code ? `${it.code} ` : ''}{it.name}</span>
                    <span>{fmt.thb(it.total)}</span>
                  </div>
                ))
              ) : null}
            </React.Fragment>
          );
        })}
      </div>

      <div className="qtotal-divider" />

      <div className="qtotal">
        <span className="lbl">Total</span>
        <span className="v mono tabular">{fmt.thb(grandTotal)}</span>
      </div>

      <div className="rule-bot" />
    </div>
  );
}
